import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PhoneIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  CloudArrowUpIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';
import { Task, TaskLead, PRIORITY_OPTIONS, STATUS_OPTIONS, getPriorityColor, getStatusColor, isOverdue, isDueToday } from '../types/Task';
import { formatDateForDisplay } from '../utils/dateUtils';
import { taskApi } from '../services/taskApi';
import TaskForm from './TaskForm';
import Layout from './Layout';
import PermissionGate from './PermissionGate';
import { noteApi } from '../services/noteApi';
import { Note } from '../types/Note';
import TaskCompletionStageModal from './TaskCompletionStageModal';
import ComposeEmailModal from './ComposeEmailModal';
import NotesForm from './NotesForm';
import { SmtpSettings } from '../types/Email';
import { emailApi } from '../services/emailApi';
import { StageSelectionData } from '../types/Automation';
import CreateCallModal from './CreateCallModal';
import RequestDocumentsModal from './RequestDocumentsModal';
import TaskDocumentUploadModal from './TaskDocumentUploadModal';
import LeadDocumentActionModal from './LeadDocumentActionModal';
import { useCallManagement } from '../contexts/CallManagementContext';
import { LeadForCall } from '../types/Call';
import { callsAPI } from '../services/callApi';
import { timelineApi } from '../services/timelineApi';
import { TimelineEvent } from '../types/Timeline';

const TaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  
  // Stage selection modal state
  const [showStageSelectionModal, setShowStageSelectionModal] = useState(false);
  const [stageSelectionData, setStageSelectionData] = useState<StageSelectionData | null>(null);
  const [stageSelectionLoading, setStageSelectionLoading] = useState(false);

  // Quick action modal state for completion requirements
  const [showCallModal, setShowCallModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showDirectUploadModal, setShowDirectUploadModal] = useState(false);
  const [showDocumentActionModal, setShowDocumentActionModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings | null>(null);
  const [selectedLeadForAction, setSelectedLeadForAction] = useState<TaskLead | null>(null);
  const [leadsForCall, setLeadsForCall] = useState<LeadForCall[]>([]);
  const [loadingLeadData, setLoadingLeadData] = useState(false);
  
  // Call management context for live calls
  const { startCall, liveCall } = useCallManagement();
  
  // Track previous liveCall state to detect when a call ends
  const [hadLiveCall, setHadLiveCall] = useState(false);

  // Active tab for the tabbed content area
  const [activeTab, setActiveTab] = useState<'details' | 'leads' | 'notes'>('details');

  // Logs (timeline events for this task)
  const [logs, setLogs] = useState<TimelineEvent[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Avatar error handling
  const [failedAvatars, setFailedAvatars] = useState<Set<number>>(new Set());

  const handleAvatarError = (userId: number) => {
    setFailedAvatars(prev => new Set(prev).add(userId));
  };

  const renderUserAvatar = (user: any, size: 'sm' | 'md' = 'sm', colorScheme: 'blue' | 'purple' = 'blue') => {
    const sizeClasses = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-7 h-7 text-xs';
    const gradientClasses = colorScheme === 'blue'
      ? 'from-blue-500 to-indigo-600'
      : 'from-purple-500 to-pink-600';
    const name = `${user.firstName || user.first_name} ${user.lastName || user.last_name}`;
    const initial = (user.firstName || user.first_name || '?').charAt(0).toUpperCase();
    const avatarUrl = user.avatar_url || user.avatarUrl;
    const hasValidAvatar = avatarUrl && !failedAvatars.has(user.id);

    if (hasValidAvatar) {
      const imgSrc = `${avatarUrl}${avatarUrl.includes('?') ? '&' : '?'}v=${user.id}`;
      return (
        <img
          key={user.id}
          src={imgSrc}
          alt={name}
          title={name}
          className={`${sizeClasses} rounded-full ring-1 ring-white flex-shrink-0 object-cover`}
          onError={() => handleAvatarError(user.id)}
        />
      );
    }

    return (
      <div
        key={user.id}
        className={`${sizeClasses} rounded-full bg-gradient-to-br ${gradientClasses} flex items-center justify-center text-white font-bold ring-1 ring-white flex-shrink-0`}
        title={name}
      >
        {initial}
      </div>
    );
  };

  // Smart back navigation
  const handleBack = () => {
    // Priority 1: Use browser history if available (most accurate)
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // Priority 2: Check for stored referrer
      try {
        const lastPage = sessionStorage.getItem('leadshive_last_page');
        if (lastPage && lastPage !== window.location.pathname) {
          navigate(lastPage);
        } else {
          // Priority 3: Fallback to tasks list
          navigate('/tasks');
        }
      } catch (e) {
        navigate('/tasks');
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchTask();
    }
  }, [id]);
  
  // Watch for live call ending - refresh task when call goes from active to null
  useEffect(() => {
    if (liveCall) {
      setHadLiveCall(true);
    } else if (hadLiveCall && !liveCall) {
      // Live call just ended, refresh task to check completion status
      setHadLiveCall(false);
      if (id) {
        fetchTask();
      }
    }
  }, [liveCall, hadLiveCall, id]);

  useEffect(() => {
    if (task && id) {
      fetchNotes();
      fetchLogs();
    }
  }, [task, id]);

  // Fetch SMTP settings for email modal
  useEffect(() => {
    const fetchSmtpSettings = async () => {
      try {
        const settings = await emailApi.getSmtpSettings();
        if (settings && settings.length > 0) {
          setSmtpSettings(settings[0]);
        }
      } catch (err) {
        console.error('Error fetching SMTP settings:', err);
      }
    };
    fetchSmtpSettings();
  }, []);

  const fetchNotes = async () => {
    if (!task || !id) return;
    
    try {
      setLoadingNotes(true);
      const taskId = parseInt(id);
      
      // Fetch notes directly by task ID (this will include notes with or without leads)
      const response = await noteApi.getNotesByTaskId(taskId);
      
      if (response.success) {
        // Sort by creation date (newest first)
        const sortedNotes = response.data.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setNotes(sortedNotes);
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const fetchLogs = async () => {
    if (!id) return;
    try {
      setLoadingLogs(true);
      const response = await timelineApi.getTimelineEvents({
        module: 'tasks',
        entityId: id,
        limit: 50,
        sortOrder: 'DESC',
      });
      if (response.success) {
        setLogs(response.data);
      }
    } catch (err) {
      console.error('Error fetching task logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchTask = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await taskApi.getTaskById(parseInt(id!));
      if (response.success) {
        setTask(response.data);
      } else {
        setError('Task not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch task');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      try {
        setDeleting(true);
        const response = await taskApi.deleteTask(parseInt(id!));
        if (response.success) {
          navigate('/tasks');
        } else {
          alert('Failed to delete task');
        }
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task');
      } finally {
        setDeleting(false);
      }
    }
  };

const handleStatusChange = async (status: string) => {
    try {
      const response = await taskApi.updateTaskStatus(parseInt(id!), status) as any;
      if (response.success) {
        setTask(prev => prev ? { ...prev, status: status as any } : null);
        fetchLogs();

        // Check if stage selection is required (for auto-created tasks with prompt mode)
        if (response.stageSelectionRequired && response.stageSelectionData) {
          setStageSelectionData(response.stageSelectionData);
          setShowStageSelectionModal(true);
        }
      } else {
        // Check if this is a completion condition error
        if (response.completionCondition) {
          const condType = response.completionCondition.type;
          let message = response.message || 'This task has completion requirements.';
          
          if (condType === 'document_upload') {
            const remaining = response.completionCondition.details?.remaining?.length || 0;
            message = `Cannot complete: ${remaining} document(s) still required.`;
          } else if (condType === 'call_completed') {
            message = 'Cannot complete: A call must be made first.';
          } else if (condType === 'form_submitted') {
            message = 'Cannot complete: A form must be submitted first.';
          }
          
          alert(message);
        } else {
          alert(response.message || 'Failed to update task status');
        }
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      const errMsg = error?.response?.data?.message || 'Failed to update task status';
      alert(errMsg);
    }
  };

  const handleStageSelection = async (selectedStageId: number) => {
    if (!stageSelectionData || !id) return;
    
    setStageSelectionLoading(true);
    try {
      const response = await taskApi.completeWithStageSelection(
        parseInt(id),
        stageSelectionData.selectionId,
        selectedStageId
      );
      
      if (response.success) {
        setShowStageSelectionModal(false);
        setStageSelectionData(null);
        console.log(`Lead stage updated to: ${response.data?.newStageName}`);
      } else {
        throw new Error(response.message || 'Failed to update lead stage');
      }
    } catch (error: any) {
      console.error('Error selecting stage:', error);
      throw error;
    } finally {
      setStageSelectionLoading(false);
    }
  };

  const handleStageSelectionClose = async () => {
    if (stageSelectionData && id) {
      try {
        await taskApi.cancelStageSelection(parseInt(id), stageSelectionData.selectionId);
      } catch (error) {
        console.error('Error cancelling stage selection:', error);
      }
    }
    setShowStageSelectionModal(false);
    setStageSelectionData(null);
  };

  const handleFormClose = () => {
    setShowEditForm(false);
    fetchTask(); // Refresh task data
  };

  const handleFormSave = (updatedTask: Task) => {
    setTask(updatedTask);
    setShowEditForm(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !task || !id) return;
    
    // Get the first associated lead for the note (optional)
    const firstLead = task.leads && task.leads.length > 0 ? task.leads[0] : null;

    try {
      setLoadingNotes(true);
      // Create note with task_id to mark it as task-specific
      // leadId is now optional - can create notes without a lead
      const response = await noteApi.createNote({
        ...(firstLead ? { leadId: firstLead.id } : {}),
        taskId: parseInt(id),
        content: newNote
      });
      
      if (response.success) {
        setNewNote('');
        setShowAddNote(false);
        fetchNotes();
        fetchLogs();
      } else {
        alert('Failed to create note');
      }
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Failed to create note');
    } finally {
      setLoadingNotes(false);
    }
  };

  const toggleNoteExpansion = (noteId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="w-5 h-5" />;
      case 'in_progress':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5" />;
      default:
        return <ClockIcon className="w-5 h-5" />;
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const response = await noteApi.deleteNote(noteId);
      if (response.success) {
        fetchNotes();
        fetchLogs();
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  // Quick action handlers for completion requirements
  const handleCallLead = async (lead: TaskLead) => {
    // Check if there's already an active call
    if (liveCall) {
      alert('There is already an active call in progress. Please end it before starting a new call.');
      return;
    }

    setSelectedLeadForAction(lead);
    setLoadingLeadData(true);
    
    try {
      // Fetch lead data with phone numbers for the call modal
      const leads = await callsAPI.getAvailableLeads();
      setLeadsForCall(leads || []);
      setShowCallModal(true);
    } catch (error) {
      console.error('Error fetching leads for call:', error);
      alert('Failed to load lead data');
    } finally {
      setLoadingLeadData(false);
    }
  };

  const handleRequestDocuments = (lead: TaskLead) => {
    setSelectedLeadForAction(lead);
    setShowDocumentModal(true);
  };

  const handleDirectUpload = (lead: TaskLead) => {
    setSelectedLeadForAction(lead);
    setShowDirectUploadModal(true);
  };

  const handleDocumentAction = (lead: TaskLead) => {
    setSelectedLeadForAction(lead);
    setShowDocumentActionModal(true);
  };

  const handleAttachForm = (lead: TaskLead) => {
    // Navigate to forms page with lead context for attachment
    // The user can then select a form submission to attach to this lead
    if (task?.completionConditionConfig?.formId) {
      navigate(`/forms?attachToLead=${lead.id}&formId=${task.completionConditionConfig.formId}`);
    } else {
      navigate(`/forms?attachToLead=${lead.id}`);
    }
  };

  const handleEmailLead = (lead: TaskLead) => {
    setSelectedLeadForAction(lead);
    setShowEmailModal(true);
  };

  const handleNoteLead = (lead: TaskLead) => {
    setSelectedLeadForAction(lead);
    setShowNoteModal(true);
  };

  const handleEmailSent = () => {
    setShowEmailModal(false);
    setSelectedLeadForAction(null);
    fetchTask();
  };

  const handleNoteCreated = () => {
    setShowNoteModal(false);
    setSelectedLeadForAction(null);
    fetchNotes();
  };

  const handleCallCreated = async (callData: any) => {
    setShowCallModal(false);
    setSelectedLeadForAction(null);
    
    try {
      // First, create the call via API
      const newCall = await callsAPI.createCall(callData);
      
      // If it's a live call, start it immediately
      if (callData.callType === 'live' && newCall.id) {
        try {
          const startedCall = await callsAPI.startCall(newCall.id);
          if (startedCall) {
            startCall(startedCall);
          }
        } catch (error) {
          console.error('Error starting live call:', error);
        }
      }
      
      // Refresh task to check if completion condition was met
      await fetchTask();
      // Also refresh logs to see the auto-completion timeline entry
      await fetchLogs();
    } catch (error) {
      console.error('Error creating call:', error);
      alert('Failed to create call');
    }
  };

  const handleDocumentRequestCreated = () => {
    setShowDocumentModal(false);
    setSelectedLeadForAction(null);
    // Refresh task data in case the document upload completes the condition
    fetchTask();
  };

  // Get the pre-selected lead for call modal (converting TaskLead to LeadForCall format)
  const getPreSelectedLeadForCall = (): LeadForCall | undefined => {
    if (!selectedLeadForAction || leadsForCall.length === 0) return undefined;
    return leadsForCall.find(l => l.id === selectedLeadForAction.id);
  };

  // Get the required call type from task config
  const getRequiredCallType = (): string | undefined => {
    const callTypes = task?.completionConditionConfig?.callTypes;
    if (callTypes && callTypes.length > 0 && !callTypes.includes('any')) {
      return callTypes[0];
    }
    return undefined;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !task) {
    return (
      <Layout title="Task Not Found" subtitle="The requested task could not be found">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error || 'Task not found'}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go back
          </button>
        </div>
      </Layout>
    );
  }

  const statusColorMap: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
    completed: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  const currentStatusStyle = statusColorMap[task.status] || statusColorMap.pending;

  return (
    <Layout title="Task Details" subtitle={task.name}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-8">

        {/* ── Breadcrumb ── */}
        <div className="flex items-center gap-1.5 mb-2 mt-1">
          <button
            onClick={handleBack}
            className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Go back"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-400">/</span>
          <button onClick={() => navigate('/tasks')} className="text-xs text-gray-500 hover:text-blue-600 font-medium">Tasks</button>
          <span className="text-xs text-gray-400">/</span>
          <span className="text-xs text-gray-600 font-medium truncate max-w-[200px]">{task.name}</span>
        </div>

        {/* ── Title + Labels (single line) ── */}
        <div className="flex items-center gap-2.5 flex-wrap mb-4">
          <h1 className="text-xl font-bold text-gray-900 mr-1">{task.name}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${getPriorityColor(task.priority)}`}>
            {PRIORITY_OPTIONS.find(p => p.value === task.priority)?.label}
          </span>
          {task.category_name && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
              <span className="w-1.5 h-1.5 rounded-full mr-1" style={{ backgroundColor: task.category_color }}></span>
              {task.category_name}
            </span>
          )}
          {task.isAutoCreated && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-600">
              Auto
            </span>
          )}
        </div>

        {/* ── Two-column body ── */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ═══ LEFT: Main content ═══ */}
          <div className="flex-1 min-w-0">

            {/* ── Compact property card with two columns ── */}
            <div className="bg-white rounded-lg border border-gray-200 mb-5 text-sm">
              <div className="flex flex-col md:flex-row">

                {/* Left: Property rows */}
                <div className="flex-1 divide-y divide-gray-100">
                  {/* Status row */}
                  <div className="flex items-center px-4 py-2.5 gap-3">
                    <span className="text-gray-500 w-24 flex-shrink-0 text-xs font-medium">Status</span>
                    <div className="flex flex-wrap gap-1">
                      {STATUS_OPTIONS.map((option) => {
                        const isActive = task.status === option.value;
                        const isDisabled = !!(task.completionConditionType && !task.completionConditionMet && option.value === 'completed');
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleStatusChange(option.value)}
                            disabled={isDisabled}
                            className={`px-2.5 py-0.5 rounded text-xs font-semibold border transition-all ${
                              isActive
                                ? currentStatusStyle + ' ring-1 ring-offset-1 ring-blue-400'
                                : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                            } ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                            title={isDisabled ? 'Complete the required action first' : ''}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Dates row */}
                  <div className="flex items-center px-4 py-2.5 gap-3">
                    <span className="text-gray-500 w-24 flex-shrink-0 text-xs font-medium">Dates</span>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
                      {task.startDate && (
                        <span className="text-gray-700">
                          <CalendarIcon className="w-3.5 h-3.5 text-gray-400 inline mr-1" />
                          Start <span className="font-medium">{formatDateForDisplay(task.startDate)}</span>
                        </span>
                      )}
                      {task.dueDate && (
                        <span className={isOverdue(task.dueDate) && task.status !== 'completed' ? 'text-red-600' : isDueToday(task.dueDate) && task.status !== 'completed' ? 'text-orange-600' : 'text-gray-700'}>
                          <CalendarIcon className={`w-3.5 h-3.5 inline mr-1 ${isOverdue(task.dueDate) && task.status !== 'completed' ? 'text-red-500' : isDueToday(task.dueDate) && task.status !== 'completed' ? 'text-orange-500' : 'text-gray-400'}`} />
                          Due <span className="font-medium">{formatDateForDisplay(task.dueDate)}</span>
                          {isOverdue(task.dueDate) && task.status !== 'completed' && <span className="ml-1 font-normal">(Overdue)</span>}
                          {isDueToday(task.dueDate) && task.status !== 'completed' && <span className="ml-1 font-normal">(Today)</span>}
                        </span>
                      )}
                      {task.reminderDate && (
                        <span className="text-gray-700">
                          <ClockIcon className="w-3.5 h-3.5 text-purple-400 inline mr-1" />
                          Reminder <span className="font-medium">{formatDateForDisplay(task.reminderDate)}</span>
                        </span>
                      )}
                      {task.status === 'completed' && task.completedAt && (
                        <span className="text-green-600 font-medium">
                          <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 inline mr-1" />
                          Completed <span className="font-semibold">{formatDateForDisplay(task.completedAt)}</span>
                        </span>
                      )}
                      {!task.startDate && !task.dueDate && !task.reminderDate && !task.completedAt && (
                        <span className="text-gray-400 italic">No dates set</span>
                      )}
                    </div>
                  </div>

                  {/* Assignees row */}
                  <div className="flex items-center px-4 py-2.5 gap-3">
                    <span className="text-gray-500 w-24 flex-shrink-0 text-xs font-medium">Assignees</span>
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      {task.assignees && task.assignees.length > 0 ? (
                        <>
                          <div className="flex -space-x-1.5">
                            {task.assignees.map((a) => renderUserAvatar(a, 'sm', 'blue'))}
                          </div>
                          <span className="text-xs text-gray-700 truncate">
                            {task.assignees.map(a => `${a.firstName || a.first_name} ${(a.lastName || a.last_name)?.[0]}.`).join(', ')}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">None</span>
                      )}
                    </div>
                  </div>

                  {/* Supervisors row */}
                  {task.supervisors && task.supervisors.length > 0 && (
                    <div className="flex items-center px-4 py-2.5 gap-3">
                      <span className="text-gray-500 w-24 flex-shrink-0 text-xs font-medium">Supervisors</span>
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <div className="flex -space-x-1.5">
                          {task.supervisors.map((s) => renderUserAvatar(s, 'sm', 'purple'))}
                        </div>
                        <span className="text-xs text-gray-700 truncate">
                          {task.supervisors.map(s => `${s.firstName || s.first_name} ${(s.lastName || s.last_name)?.[0]}.`).join(', ')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Requirement row */}
                  {task.completionConditionType && (
                    <div className="flex items-center px-4 py-2.5 gap-3">
                      <span className="text-gray-500 w-24 flex-shrink-0 text-xs font-medium">Requirement</span>
                      <div className="flex items-center gap-1.5 text-xs">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          task.completionConditionMet 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {task.completionConditionMet ? 'Met' : 'Pending'}
                        </span>
                        <span className="text-gray-600">
                          {task.completionConditionType === 'document_upload' && 'Document Upload'}
                          {task.completionConditionType === 'call_completed' && 'Call Completed'}
                          {task.completionConditionType === 'form_submitted' && 'Form Submitted'}
                          {task.completionConditionType === 'email_sent' && 'Email Sent'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Attached leads */}
                {task.leads && task.leads.length > 0 && (
                  <div className="md:w-80 lg:w-96 flex-shrink-0 md:border-l border-t md:border-t-0 border-gray-200">
                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Attached Leads</span>
                      <span className="text-[10px] text-gray-400 font-medium">{task.leads.length}</span>
                    </div>
                    <div className="divide-y divide-gray-50 max-h-48 overflow-y-auto">
                      {task.leads.map((lead) => (
                        <div key={lead.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors group">
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                              <UserIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-900 truncate leading-tight">
                                {lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5 ml-2">
                            {/* Call Icon - Always visible, highlighted if required */}
                            <PermissionGate module="calls" permission="create">
                              <button
                                onClick={() => handleCallLead(lead)}
                                disabled={loadingLeadData || !!liveCall}
                                title={task.completionConditionType === 'call_completed' && !task.completionConditionMet ? "Call (Required)" : "Call"}
                                className={`p-1.5 rounded-md transition-all ${
                                  task.completionConditionType === 'call_completed' && !task.completionConditionMet
                                    ? liveCall ? 'bg-gray-100 text-gray-300' : 'bg-green-100 text-green-600 hover:bg-green-200 ring-1 ring-green-300'
                                    : liveCall ? 'text-gray-300' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                              >
                                <PhoneIcon className="h-3.5 w-3.5" />
                              </button>
                            </PermissionGate>

                            {/* Email Icon - Always visible, highlighted if required */}
                            <PermissionGate module="emails" permission="create">
                              <button
                                onClick={() => task.completionConditionType === 'email_sent' && !task.completionConditionMet ? navigate(`/leads/${lead.id}?tab=emails`) : handleEmailLead(lead)}
                                title={task.completionConditionType === 'email_sent' && !task.completionConditionMet ? "Email (Required)" : "Email"}
                                className={`p-1.5 rounded-md transition-all ${
                                  task.completionConditionType === 'email_sent' && !task.completionConditionMet
                                    ? 'bg-orange-100 text-orange-600 hover:bg-orange-200 ring-1 ring-orange-300'
                                    : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                                }`}
                              >
                                <EnvelopeIcon className="h-3.5 w-3.5" />
                              </button>
                            </PermissionGate>

                            {/* Document Icon - Always visible, highlighted if required */}
                            <PermissionGate module="documents" permission="create">
                              <button
                                onClick={() => task.completionConditionType === 'document_upload' && !task.completionConditionMet ? handleDirectUpload(lead) : handleDocumentAction(lead)}
                                title={task.completionConditionType === 'document_upload' && !task.completionConditionMet ? "Documents (Required)" : "Documents"}
                                className={`p-1.5 rounded-md transition-all ${
                                  task.completionConditionType === 'document_upload' && !task.completionConditionMet
                                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 ring-1 ring-blue-300'
                                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                              >
                                <DocumentTextIcon className="h-3.5 w-3.5" />
                              </button>
                            </PermissionGate>

                            {/* Form Icon - Always visible, highlighted if required */}
                            <PermissionGate module="forms" permission="view">
                              <button
                                onClick={() => handleAttachForm(lead)}
                                title={task.completionConditionType === 'form_submitted' && !task.completionConditionMet ? "Forms (Required)" : "Forms"}
                                className={`p-1.5 rounded-md transition-all ${
                                  task.completionConditionType === 'form_submitted' && !task.completionConditionMet
                                    ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 ring-1 ring-purple-300'
                                    : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                                }`}
                              >
                                <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                              </button>
                            </PermissionGate>

                            {/* Notes button - always visible */}
                            <button
                              onClick={() => handleNoteLead(lead)}
                              title="Notes"
                              className="p-1.5 rounded-md text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-all"
                            >
                              <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                            </button>

                            {/* View lead button */}
                            <button
                              onClick={() => navigate(`/leads/${lead.id}`)}
                              className="text-[10px] font-medium text-blue-600 hover:text-blue-700 px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex gap-6 -mb-px">
                {[
                  { key: 'details' as const, label: 'Details' },
                  ...(task.leads && task.leads.length > 0 ? [{ key: 'leads' as const, label: `Leads (${task.leads.length})` }] : []),
                  { key: 'notes' as const, label: `Notes (${notes.length})` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`pb-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* ── Tab Content ── */}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-5">
                {/* Description */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h3>
                  {task.description ? (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-white rounded-lg border border-gray-200 p-4">
                      {task.description}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No description provided</p>
                  )}
                </div>

                {/* Completion Requirement Detail */}
                {task.completionConditionType && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Completion Requirement</h3>
                    <div className={`rounded-lg border p-4 ${
                      task.completionConditionMet 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <span className="text-xl mt-0.5">
                          {task.completionConditionType === 'document_upload' && '📄'}
                          {task.completionConditionType === 'call_completed' && '📞'}
                          {task.completionConditionType === 'form_submitted' && '📋'}
                          {task.completionConditionType === 'email_sent' && '✉️'}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-sm font-semibold ${task.completionConditionMet ? 'text-green-800' : 'text-amber-800'}`}>
                              {task.completionConditionType === 'document_upload' && 'Document Upload Required'}
                              {task.completionConditionType === 'call_completed' && 'Call Required'}
                              {task.completionConditionType === 'form_submitted' && 'Form Submission Required'}
                              {task.completionConditionType === 'email_sent' && 'Email Required'}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              task.completionConditionMet ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                            }`}>
                              {task.completionConditionMet ? 'Completed' : 'Pending'}
                            </span>
                          </div>
                          <p className={`text-xs ${task.completionConditionMet ? 'text-green-700' : 'text-amber-700'}`}>
                            {task.completionConditionType === 'document_upload' && (
                              task.completionConditionMet 
                                ? 'All required documents have been uploaded.' 
                                : 'Upload the required documents for attached leads.'
                            )}
                            {task.completionConditionType === 'call_completed' && (
                              task.completionConditionMet 
                                ? 'The required call has been completed.' 
                                : 'Complete a call with the attached leads.'
                            )}
                            {task.completionConditionType === 'form_submitted' && (
                              task.completionConditionMet 
                                ? 'The required form has been submitted.' 
                                : 'Submit the required form for attached leads.'
                            )}
                            {task.completionConditionType === 'email_sent' && (
                              task.completionConditionMet 
                                ? 'An email has been sent to the lead.' 
                                : 'Send an email to the attached leads.'
                            )}
                          </p>
                          {task.completionConditionMet && task.completionConditionMetAt && (
                            <p className="text-xs text-green-600 mt-1.5 font-medium">
                              Completed on {new Date(task.completionConditionMetAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Auto-created info */}
                {task.isAutoCreated && task.ruleName && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Automation</h3>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
                      <span className="text-lg">🤖</span>
                      <div>
                        <p className="text-sm font-medium text-purple-800">Created by automation rule</p>
                        <p className="text-xs text-purple-600">{task.ruleName}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && task.leads && task.leads.length > 0 && (
              <div>
                {task.completionConditionType && !task.completionConditionMet && (
                  <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-xs text-amber-700 font-medium">Actions required - use the icons to complete the requirement for each lead.</span>
                  </div>
                )}
                <div className="space-y-2">
                  {task.leads.map((lead) => (
                    <div key={lead.id} className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown Lead'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {lead.email}{lead.phone ? ` \u00B7 ${lead.phone}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-3">
                        {/* Call Icon - Always visible, highlighted if required */}
                        <PermissionGate module="calls" permission="create">
                          <button
                            onClick={() => handleCallLead(lead)}
                            disabled={loadingLeadData || !!liveCall}
                            title={task.completionConditionType === 'call_completed' && !task.completionConditionMet ? "Call (Required)" : "Call"}
                            className={`p-2 rounded-md transition-all ${
                              task.completionConditionType === 'call_completed' && !task.completionConditionMet
                                ? liveCall ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-green-100 text-green-600 hover:bg-green-200 ring-1 ring-green-300'
                                : liveCall ? 'text-gray-300' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                          >
                            <PhoneIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>

                        {/* Email Icon - Always visible, highlighted if required */}
                        <PermissionGate module="emails" permission="create">
                          <button
                            onClick={() => task.completionConditionType === 'email_sent' && !task.completionConditionMet ? navigate(`/leads/${lead.id}?tab=emails`) : handleEmailLead(lead)}
                            title={task.completionConditionType === 'email_sent' && !task.completionConditionMet ? "Email (Required)" : "Email"}
                            className={`p-2 rounded-md transition-all ${
                              task.completionConditionType === 'email_sent' && !task.completionConditionMet
                                ? 'bg-orange-100 text-orange-600 hover:bg-orange-200 ring-1 ring-orange-300'
                                : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                            }`}
                          >
                            <EnvelopeIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>

                        {/* Document Icon - Always visible, highlighted if required */}
                        <PermissionGate module="documents" permission="create">
                          <button
                            onClick={() => task.completionConditionType === 'document_upload' && !task.completionConditionMet ? handleDirectUpload(lead) : handleDocumentAction(lead)}
                            title={task.completionConditionType === 'document_upload' && !task.completionConditionMet ? "Documents (Required)" : "Documents"}
                            className={`p-2 rounded-md transition-all ${
                              task.completionConditionType === 'document_upload' && !task.completionConditionMet
                                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 ring-1 ring-blue-300'
                                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>

                        {/* Form Icon - Always visible, highlighted if required */}
                        <PermissionGate module="forms" permission="view">
                          <button
                            onClick={() => handleAttachForm(lead)}
                            title={task.completionConditionType === 'form_submitted' && !task.completionConditionMet ? "Forms (Required)" : "Forms"}
                            className={`p-2 rounded-md transition-all ${
                              task.completionConditionType === 'form_submitted' && !task.completionConditionMet
                                ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 ring-1 ring-purple-300'
                                : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                            }`}
                          >
                            <ClipboardDocumentListIcon className="h-4 w-4" />
                          </button>
                        </PermissionGate>

                        {/* Notes button - always visible */}
                        <button
                          onClick={() => handleNoteLead(lead)}
                          title="Notes"
                          className="p-2 rounded-md text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-all"
                        >
                          <ChatBubbleLeftIcon className="h-4 w-4" />
                        </button>

                        {/* View lead button */}
                        <button
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          className="ml-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div>
                {/* Comment input at top */}
                <div className="mb-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold mt-0.5">
                      {(() => {
                        try {
                          const u = JSON.parse(localStorage.getItem('leadshive_user') || '{}');
                          return (u.firstName || u.first_name || 'U').charAt(0).toUpperCase();
                        } catch { return 'U'; }
                      })()}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        rows={2}
                        placeholder="Write a note..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-gray-50 focus:bg-white transition-colors"
                        onFocus={() => setShowAddNote(true)}
                      />
                      {(showAddNote || newNote.trim()) && (
                        <div className="mt-2 flex justify-end gap-2">
                          <button
                            onClick={() => { setShowAddNote(false); setNewNote(''); }}
                            className="px-3 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddNote}
                            disabled={!newNote.trim() || loadingNotes}
                            className="px-4 py-1 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {loadingNotes ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes list */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {loadingNotes ? (
                    <p className="text-sm text-gray-400 text-center py-6">Loading notes...</p>
                  ) : notes.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">No notes yet. Write the first one above.</p>
                  ) : (
                    notes.map((note) => {
                      const isExpanded = expandedNotes.has(note.id);
                      const isTruncated = note.content.length > 200;
                      const displayContent = isTruncated && !isExpanded
                        ? note.content.substring(0, 200) + '...'
                        : note.content;

                      return (
                        <div key={note.id} className="flex items-start gap-3 group">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-xs font-bold mt-0.5">
                            {note.creatorName?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-gray-900">{note.creatorName || 'Unknown'}</span>
                              <span className="text-xs text-gray-400">{new Date(note.createdAt).toLocaleString()}</span>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="ml-auto opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                                title="Delete note"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{displayContent}</p>
                            {isTruncated && (
                              <button
                                onClick={() => toggleNoteExpansion(note.id)}
                                className="mt-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                              >
                                {isExpanded ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ═══ RIGHT: Sidebar ═══ */}
          <div className="w-full lg:w-56 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 sticky top-4">

              {/* Action buttons */}
              <div className="p-2.5 space-y-1 border-b border-gray-100">
                <PermissionGate module="tasks" permission="edit">
                  <button
                    onClick={handleEdit}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
                  >
                    <PencilIcon className="w-3.5 h-3.5 text-gray-400" />
                    Edit Task
                  </button>
                </PermissionGate>
                <PermissionGate module="tasks" permission="delete">
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors text-left disabled:opacity-50"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                    {deleting ? 'Deleting...' : 'Delete Task'}
                  </button>
                </PermissionGate>
              </div>

              {/* Logs */}
              <div className="px-3 py-2.5">
                <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Logs</h4>
                <div className="max-h-[400px] overflow-y-auto -mr-1 pr-1">
                  {loadingLogs ? (
                    <p className="text-[11px] text-gray-400 text-center py-3">Loading...</p>
                  ) : logs.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-3">No activity yet</p>
                  ) : (
                    <div className="relative">
                      {/* Vertical tree line */}
                      <div className="absolute left-[4px] top-2 bottom-2 w-px bg-gray-200"></div>

                      <div className="space-y-0">
                        {logs.map((log, idx) => {
                          const isCreate = log.action === 'create';
                          const isDelete = log.action === 'delete';
                          const isStatusChange = log.changes && log.changes.status;
                          const isNoteEvent = log.metadata?.type === 'note_added' || log.metadata?.type === 'note_deleted';
                          
                          let dotColor = 'bg-gray-400';
                          if (isCreate) dotColor = 'bg-blue-500';
                          else if (isDelete) dotColor = 'bg-red-500';
                          else if (isStatusChange) dotColor = 'bg-amber-500';
                          else if (isNoteEvent) dotColor = 'bg-purple-500';
                          else dotColor = 'bg-gray-400';

                          const userName = log.userName || 'System';
                          const timeStr = new Date(log.createdAt).toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          });

                          return (
                            <div key={log.id} className="relative pl-4 pb-3 group">
                              {/* Tree dot */}
                              <div className={`absolute left-[2px] top-1.5 w-[5px] h-[5px] rounded-full ${dotColor} ring-2 ring-white z-10`}></div>

                              <div className="min-w-0">
                                <p className="text-[11px] text-gray-700 leading-snug">
                                  {isCreate && (
                                    <><span className="font-semibold">{userName}</span> created this task</>
                                  )}
                                  {isStatusChange && (
                                    <>
                                      <span className="font-semibold">{userName}</span> changed status from{' '}
                                      <span className="font-semibold text-gray-500">{log.changes!.status.from}</span>
                                      {' '}to{' '}
                                      <span className="font-semibold text-blue-600">{log.changes!.status.to}</span>
                                    </>
                                  )}
                                  {!isCreate && !isStatusChange && !isDelete && !isNoteEvent && log.changes && Object.keys(log.changes).length > 0 && (
                                    <>
                                      <span className="font-semibold">{userName}</span> updated{' '}
                                      {Object.keys(log.changes).map((field, i) => (
                                        <span key={field}>
                                          {i > 0 && ', '}
                                          <span className="text-gray-500">{field.replace(/_/g, ' ')}</span>
                                          {log.changes![field].from && (
                                            <> from <span className="font-medium">{String(log.changes![field].from).substring(0, 20)}</span></>
                                          )}
                                          {' '}to <span className="font-medium text-blue-600">{String(log.changes![field].to).substring(0, 20)}</span>
                                        </span>
                                      ))}
                                    </>
                                  )}
                                  {!isCreate && !isStatusChange && !isDelete && !isNoteEvent && (!log.changes || Object.keys(log.changes).length === 0) && (
                                    <><span className="font-semibold">{userName}</span> {log.description}</>
                                  )}
                                  {isNoteEvent && (
                                    <>
                                      <span className="font-semibold">{userName}</span>{' '}
                                      {log.metadata?.type === 'note_added' ? 'added a note' : 'deleted a note'}
                                      {log.metadata?.content && (
                                        <span className="text-gray-400 block text-[10px] mt-0.5 italic truncate">"{log.metadata.content}"</span>
                                      )}
                                    </>
                                  )}
                                  {isDelete && !isNoteEvent && (
                                    <><span className="font-semibold">{userName}</span> deleted this task</>
                                  )}
                                </p>
                                <p className="text-[10px] text-gray-400 mt-0.5">{timeStr}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditForm && (
        <TaskForm
          task={task}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}

      {stageSelectionData && (
        <TaskCompletionStageModal
          isOpen={showStageSelectionModal}
          onClose={handleStageSelectionClose}
          onSelectStage={handleStageSelection}
          selectionData={stageSelectionData}
          isLoading={stageSelectionLoading}
        />
      )}

      {showCallModal && (
        <CreateCallModal
          leads={leadsForCall}
          onClose={() => {
            setShowCallModal(false);
            setSelectedLeadForAction(null);
          }}
          onCallCreated={handleCallCreated}
          preSelectedLead={getPreSelectedLeadForCall()}
          lockLeadSelection={true}
          preSelectedCallType={getRequiredCallType()}
        />
      )}

      {showDocumentModal && selectedLeadForAction && (
        <RequestDocumentsModal
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedLeadForAction(null);
          }}
          onRequestCreated={handleDocumentRequestCreated}
          preSelectedLeadId={selectedLeadForAction.id}
          lockLeadSelection={true}
          preSelectedDocumentTypeIds={task?.completionConditionConfig?.documentTypeIds}
        />
      )}

      {/* Task Document Upload Modal */}
      {selectedLeadForAction && (
        <TaskDocumentUploadModal
          isOpen={showDirectUploadModal}
          onClose={() => {
            setShowDirectUploadModal(false);
            setSelectedLeadForAction(null);
          }}
          onSuccess={() => {
            setShowDirectUploadModal(false);
            setSelectedLeadForAction(null);
            fetchTask();
          }}
          lead={{
            id: selectedLeadForAction.id,
            firstName: selectedLeadForAction.first_name || selectedLeadForAction.name?.split(' ')[0] || '',
            lastName: selectedLeadForAction.last_name || selectedLeadForAction.name?.split(' ').slice(1).join(' ') || '',
            email: selectedLeadForAction.email || ''
          }}
          documentTypeIds={task?.completionConditionConfig?.documentTypeIds || []}
          taskId={task?.id}
        />
      )}

      {/* Lead Document Action Modal (for non-completion document actions) */}
      {selectedLeadForAction && (
        <LeadDocumentActionModal
          isOpen={showDocumentActionModal}
          onClose={() => {
            setShowDocumentActionModal(false);
            setSelectedLeadForAction(null);
          }}
          onSuccess={() => {
            setShowDocumentActionModal(false);
            setSelectedLeadForAction(null);
            fetchTask();
          }}
          lead={{
            id: selectedLeadForAction.id,
            firstName: selectedLeadForAction.first_name || selectedLeadForAction.name?.split(' ')[0] || '',
            lastName: selectedLeadForAction.last_name || selectedLeadForAction.name?.split(' ').slice(1).join(' ') || '',
            email: selectedLeadForAction.email || ''
          }}
          preSelectedDocumentTypeIds={[]}
          taskId={task?.id}
        />
      )}

      {/* Email Compose Modal */}
      {showEmailModal && selectedLeadForAction && (
        <ComposeEmailModal
          onClose={() => {
            setShowEmailModal(false);
            setSelectedLeadForAction(null);
          }}
          onEmailSent={handleEmailSent}
          smtpSettings={smtpSettings}
          preFilledToEmail={selectedLeadForAction.email || ''}
        />
      )}

      {/* Note Creation Modal */}
      {showNoteModal && selectedLeadForAction && (
        <NotesForm
          preSelectedLead={{
            id: selectedLeadForAction.id,
            name: selectedLeadForAction.name || `${selectedLeadForAction.first_name || ''} ${selectedLeadForAction.last_name || ''}`.trim() || 'Unknown',
            email: selectedLeadForAction.email || '',
            phone: selectedLeadForAction.phone || ''
          }}
          onClose={() => {
            setShowNoteModal(false);
            setSelectedLeadForAction(null);
          }}
          onSuccess={handleNoteCreated}
        />
      )}
    </Layout>
  );
};

export default TaskDetail;
